import {
  Copy,
  Plus,
  Phone,
  ShieldCheck,
  Trash2,
  UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ContactPersonCard } from "@/components/onboarding/formular-notificare/ContactPersonCard";
import { FieldTooltip } from "@/components/onboarding/shared/FieldTooltip";
import { ONBOARDING_TOOLTIPS } from "@/utils/onboardingTooltips";
import type { ContactData, ContactPerson } from "@/types/contact";

type ContactPersonKey =
  | "reprezentantLegal"
  | "responsabilSecuritate"
  | "contactOperational";

type ContactPersonField = keyof (ContactPerson & { functie: string });

type ContactsSectionProps = {
  contactData: ContactData;
  onContactFieldChange: (
    person: ContactPersonKey,
    field: ContactPersonField,
    value: string
  ) => void;
  onContactMetaChange: (
    field: "adreseIP" | "sistemeAfectate",
    value: string[] | string
  ) => void;
  fieldErrors: Record<string, string>;
};

function ContactsSection({
  contactData,
  onContactFieldChange,
  onContactMetaChange,
  fieldErrors,
}: ContactsSectionProps) {
  const copyContact = (
    source: ContactPersonKey,
    target: ContactPersonKey,
    defaultFunctie: string
  ) => {
    const sourceData = contactData[source];
    if (!sourceData.nume || !sourceData.prenume) return;
    const fields: (keyof (ContactPerson & { functie: string }))[] = [
      "nume",
      "prenume",
      "email",
      "telefon",
    ];
    for (const field of fields) {
      onContactFieldChange(target, field, sourceData[field]);
    }
    onContactFieldChange(target, "functie", defaultFunctie);
  };

  const canCopyFrom = (person: ContactPersonKey) =>
    Boolean(contactData[person].nume && contactData[person].prenume);

  const handleAddIP = () => {
    onContactMetaChange("adreseIP", [...contactData.adreseIP, ""]);
  };

  const handleRemoveIP = (index: number) => {
    const updated = contactData.adreseIP.filter((_, i) => i !== index);
    onContactMetaChange("adreseIP", updated.length > 0 ? updated : [""]);
  };

  const handleIPChange = (index: number, value: string) => {
    const updated = [...contactData.adreseIP];
    updated[index] = value;
    onContactMetaChange("adreseIP", updated);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ContactPersonCard
          title="Reprezentant Legal"
          icon={UserCheck}
          tooltip={ONBOARDING_TOOLTIPS.reprezentant_legal}
          fieldTooltips={{ email: ONBOARDING_TOOLTIPS.email_contact }}
          person={contactData.reprezentantLegal}
          onFieldChange={(field, value) =>
            onContactFieldChange("reprezentantLegal", field, value)
          }
          personKey="reprezentantLegal"
          fieldErrors={fieldErrors}
          headerActions={<></>}
        />
        <ContactPersonCard
          title="Responsabil Securitate"
          icon={ShieldCheck}
          tooltip={ONBOARDING_TOOLTIPS.responsabil_securitate}
          fieldTooltips={{ email: ONBOARDING_TOOLTIPS.email_contact }}
          person={contactData.responsabilSecuritate}
          onFieldChange={(field, value) =>
            onContactFieldChange("responsabilSecuritate", field, value)
          }
          personKey="responsabilSecuritate"
          fieldErrors={fieldErrors}
          headerActions={
            <button
              type="button"
              onClick={() =>
                copyContact(
                  "reprezentantLegal",
                  "responsabilSecuritate",
                  "Responsabil Securitate IT"
                )
              }
              disabled={!canCopyFrom("reprezentantLegal")}
              className="inline-flex items-center gap-1 text-xs text-ds-text-500 hover:text-ds-text-600 hover:underline cursor-pointer disabled:text-ds-border-300 disabled:pointer-events-none mt-1"
            >
              <Copy className="h-3 w-3" />
              Copiază din Reprezentant Legal
            </button>
          }
        />
        <ContactPersonCard
          title="Contact Operațional"
          icon={Phone}
          tooltip={ONBOARDING_TOOLTIPS.contact_operational}
          fieldTooltips={{ email: ONBOARDING_TOOLTIPS.email_contact }}
          person={contactData.contactOperational}
          onFieldChange={(field, value) =>
            onContactFieldChange("contactOperational", field, value)
          }
          personKey="contactOperational"
          fieldErrors={fieldErrors}
          headerActions={
            <div className="flex flex-col gap-0.5 mt-1">
              <button
                type="button"
                onClick={() =>
                  copyContact(
                    "reprezentantLegal",
                    "contactOperational",
                    "Manager Operațiuni"
                  )
                }
                disabled={!canCopyFrom("reprezentantLegal")}
                className="inline-flex items-center gap-1 text-xs text-ds-text-500 hover:text-ds-text-600 hover:underline cursor-pointer disabled:text-ds-border-300 disabled:pointer-events-none"
              >
                <Copy className="h-3 w-3" />
                Copiază din Reprezentant Legal
              </button>
              <button
                type="button"
                onClick={() =>
                  copyContact(
                    "responsabilSecuritate",
                    "contactOperational",
                    "Contact Tehnic 24/7"
                  )
                }
                disabled={!canCopyFrom("responsabilSecuritate")}
                className="inline-flex items-center gap-1 text-xs text-ds-text-500 hover:text-ds-text-600 hover:underline cursor-pointer disabled:text-ds-border-300 disabled:pointer-events-none"
              >
                <Copy className="h-3 w-3" />
                Copiază din Responsabil Securitate
              </button>
            </div>
          }
        />
      </div>

      <div className="space-y-4 border-t border-ds-border-200 pt-4">
        <div>
          <Label className="text-sm font-medium inline-flex items-center gap-1">
            Plaje de Adrese IP Publice
            <FieldTooltip content={ONBOARDING_TOOLTIPS.adrese_ip} />
          </Label>
          <div className="space-y-2 mt-2">
            {contactData.adreseIP.map((ip, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={ip}
                  onChange={(e) => handleIPChange(index, e.target.value)}
                  placeholder="ex: 185.10.20.0/24"
                  className="text-xs md:text-sm"
                />
                {contactData.adreseIP.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveIP(index)}
                  >
                    <Trash2 className="w-4 h-4 text-ds-text-400" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddIP}
              className="text-xs"
            >
              <Plus className="w-3.5 h-3.5" />
              Adaugă IP
            </Button>
          </div>
        </div>

        <div>
          <Label
            htmlFor="sisteme-afectate"
            className="text-sm font-medium inline-flex items-center gap-1"
          >
            Sisteme Afectate
            <FieldTooltip content={ONBOARDING_TOOLTIPS.sisteme_afectate} />
          </Label>
          <Textarea
            id="sisteme-afectate"
            value={contactData.sistemeAfectate}
            onChange={(e) =>
              onContactMetaChange("sistemeAfectate", e.target.value)
            }
            placeholder="Descrieți sistemele informatice afectate..."
            className="text-xs md:text-sm mt-1"
            rows={3}
          />
        </div>
      </div>
    </div>
  );
}

export default ContactsSection;
